import { useEffect } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiGetPolicyInfo } from "utils/API/TrxNowAPI";
import { PolicyLookupSpinner } from "webapp/components/PolicyLookupSpinner";
import FormControl from "@mui/material/FormControl";
import { TextField } from "@mui/material";
import { devLog } from "utils/devLog";
import "./APCOMemberInformationForm.scss";

export default function APCOMemberInformationForm(props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [phoneNumber] = useState(props.currState.MemberLookup.phoneNumber || "");
  const [firstName] = useState(props.currState.FirstName || "");
  const [lastName] = useState(props.currState.LastName || "");
  const [memberNumber] = useState(props.currState.MemberLookup.memberNumber || "");
  const [last8Vin] = useState(props.currState.MemberLookup.last8Vin || "");

  const [searchType] = useState(props.currState.MemberLookup.searchType);
  const [memberInfo, setMemberInfo] = useState(undefined);

  const [displaySelectionComponent, setDisplaySelectionComponent] = useState(false);
  const [partnerCode] = useState(props.currState.localGroup);
  const [yesHasTrouble, setYesHasTrouble] = useState(props.MNFMemberInfoData?.havingTrouble || false);

  useEffect(() => {
    onSubmit();
  }, []);

  const onLoad = (success) => {
    if (typeof props.onLoad === "function") {
      props.onLoad(success);
      setDisplaySelectionComponent(success);
    }
  };

  const onMemberInfo = (memberInfo, contactInfo) => {
    if (typeof props.onMemberInfo === "function") {
      props.onMemberInfo(memberInfo, contactInfo);
    }
  };

  const resolveContact = (info) => {
    if (!info) return null;
    if (info.contacts?.length) {
      return info.contacts.find((c) => c.contactRef === info.contactRef) || info.contacts[0];
    }
    return info.contactRef ? { contactRef: info.contactRef } : null;
  };

  const applyMemberInfo = (info) => {
    setMemberInfo(info);
    onMemberInfo(info, resolveContact(info));
    onLoad(true);
  };

  const onSubmit = (e) => {
    setError("");
    setLoading(true);
    apiGetPolicyInfo({
      data: {
        providerRef: props.currState.providerRef,
        firstName: firstName,
        lastName: lastName,
        phoneNumber: phoneNumber,
        last8Vin: last8Vin,
        memberNumber: memberNumber,
        searchType: searchType,
        partnerCode: partnerCode,
      },
      companyParams: props.currState.currentCompanyParams,
    })
      .then((json) => {
        if (typeof json.data.memberInfo === "object") {
          if (!json.data.error) {
            applyMemberInfo(json.data.memberInfo);
          } else {
            setMemberInfo(undefined);
            setError(json.data.error.message);
            onLoad(false);
          }
        } else if (typeof json.data.error === "object") {
          setMemberInfo(undefined);
          setError(json.data.error.message);
          onLoad(false);
        } else {
          setMemberInfo(undefined);
          setError(t("BWC.PolicyInformation.UnableToFindMember"));
          onLoad(false);
        }
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        setMemberInfo(undefined);
        setError(t("BWC.PolicyInformation.UnableToFindMember"));
        onLoad(false);
      });
  };

  const onUserBack = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    if (typeof props.onUserBack === "function") {
      props.onUserBack();
    }
  };

  const onClickYesHasTrouble = () => {
    const nextHavingTrouble = !yesHasTrouble;
    setYesHasTrouble(nextHavingTrouble);
    // Keep footer visible while the MNF form is open (same pattern as GKW/CARS)
    onLoad(nextHavingTrouble);
    if (typeof props.setMNFMemberInfoData === "function") {
      props.setMNFMemberInfoData({
        ...props.MNFMemberInfoData,
        havingTrouble: nextHavingTrouble,
      });
    }
    if (typeof props.onHavingTroubleChange === "function") {
      props.onHavingTroubleChange(nextHavingTrouble);
    }
  };

  const updateMNFField = (field, value) => {
    if (typeof props.setMNFMemberInfoData === "function") {
      props.setMNFMemberInfoData({
        ...props.MNFMemberInfoData,
        [field]: value,
      });
    }
  };

  return (
    <>
      <div className="formContainer">
        {memberInfo && !error && displaySelectionComponent && !yesHasTrouble && (
          <div className="policyData">
            <div className="policyHeader">
              <h4>{t("description.ContractInformationHeader2")}</h4>
              <hr />
              <div className="policy-info-line">
                <b>{t("description.Name")}:</b> {memberInfo.name}
              </div>
              <hr />
              <div className="policy-info-line">
                <b>{t("description.ConfirmCustomerPhoneNumber")}:</b>{" "}
                {memberInfo.phoneNumber ? memberInfo.phoneNumber : "N/A"}
              </div>
              <hr />
              <div className="policy-info-line">
                <b>{t("description.ConfirmVehicleYear")}:</b> {memberInfo.year || "N/A"}
              </div>
              <hr />
              <div className="policy-info-line">
                <b>{t("description.ConfirmVehicleMake")}:</b> {memberInfo.make || "N/A"}
              </div>
              <hr />
              <div className="policy-info-line">
                <b>{t("description.ConfirmVehicleModel")}:</b> {memberInfo.model || "N/A"}
              </div>
              <hr />
              <div className="policy-info-line">
                <b>{t("ATF.PolicyInformation.ConfirmCustomerLast8Vin")}:</b> {memberInfo.last8Vin || "N/A"}
              </div>
              <hr />
            </div>
          </div>
        )}
      </div>
      {error ? (
        <div className="errorSectionCYSU">
          <b className="error" style={{ color: "red" }}>
            {error}
          </b>
          <div className="member-search-failing-section">
            <h6>{t("description.TroubleFindingAccount")}</h6>
            <div className="member-search-failing-actions">
              <button
                type="button"
                className={`member-search-failing-btn ${
                  yesHasTrouble ? "member-search-failing-btn--primary" : "member-search-failing-btn--secondary"
                }`}
                onClick={onClickYesHasTrouble}
              >
                {t("description.Yes")}
              </button>
              <button
                type="button"
                className="member-search-failing-btn member-search-failing-btn--secondary"
                onClick={onUserBack}
              >
                {t("description.SearchAgain")}
              </button>
            </div>
          </div>
          {yesHasTrouble && (
            <div className="member-search-failing-section new-contact-input-container" style={{ paddingTop: "1rem" }}>
              <h6 style={{ marginBottom: "0" }}>{t("description.FillUpInformation")}</h6>
              <FormControl className="MNF-input-box">
                <TextField
                  id="outlined-basic-firstName"
                  label={t("description.ConfirmCustomerFirstName")}
                  variant="outlined"
                  size="small"
                  fullWidth={true}
                  value={props.MNFMemberInfoData?.firstName || ""}
                  type="string"
                  onChange={(e) => {
                    updateMNFField("firstName", e.target.value);
                  }}
                />
              </FormControl>

              <FormControl className="MNF-input-box">
                <TextField
                  id="outlined-basic-lastName"
                  label={t("description.ConfirmCustomerLastName")}
                  variant="outlined"
                  size="small"
                  fullWidth={true}
                  value={props.MNFMemberInfoData?.lastName || ""}
                  type="string"
                  onChange={(e) => {
                    updateMNFField("lastName", e.target.value);
                  }}
                />
              </FormControl>

              <FormControl className="MNF-input-box">
                <TextField
                  id="outlined-basic-phoneNumber"
                  label={t("description.ConfirmCustomerPhoneNumber")}
                  variant="outlined"
                  size="small"
                  fullWidth={true}
                  value={props.MNFMemberInfoData?.phoneNumber || ""}
                  type="string"
                  onChange={(e) => {
                    updateMNFField("phoneNumber", e.target.value);
                  }}
                />
              </FormControl>

              <FormControl className="MNF-input-box">
                <TextField
                  id="outlined-basic-last8Vin"
                  label={t("ATF.PolicyInformation.ConfirmCustomerLast8Vin")}
                  variant="outlined"
                  size="small"
                  fullWidth={true}
                  value={props.MNFMemberInfoData?.last8Vin || ""}
                  type="string"
                  inputProps={{ maxLength: 8 }}
                  onChange={(e) => {
                    const nextValue = (e.target.value || "").toUpperCase().trim().slice(0, 8);
                    devLog("last8Vin change:", nextValue);
                    updateMNFField("last8Vin", nextValue);
                  }}
                />
              </FormControl>
            </div>
          )}
        </div>
      ) : (
        <></>
      )}
      {loading ? <PolicyLookupSpinner title={`ContractLookupTitleBWC`} message={`PolicyLookupMessage`} /> : <></>}
    </>
  );
}
